/**
 * The organizer's message after registration: text paragraphs plus at most
 * one button, stored as JSON blocks and never as HTML. Every reader renders
 * it through its own escaped template. The database CHECK
 * (registration_message_is_valid) enforces the same rules as parseMessage.
 */

export const MESSAGE_MAX_TEXT = 1000;
export const MESSAGE_MAX_PARAGRAPHS = 50;
export const BUTTON_MAX_LABEL = 40;
export const URL_MAX = 2048;

/** Where a message goes: the confirmation email, the pop-up after registration, or both. */
export const MESSAGE_CHANNELS = ['both', 'email', 'platform'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNELS)[number];

export function readChannel(value: unknown): MessageChannel {
  return MESSAGE_CHANNELS.includes(value as MessageChannel) ? (value as MessageChannel) : 'both';
}

export const BUTTON_COLORS = ['confy', 'telegram', 'youtube', 'viber', 'instagram', 'green', 'dark', 'grey'] as const;
export type ButtonColor = (typeof BUTTON_COLORS)[number];

/** Background and text per swatch; text is picked for contrast, never by the organizer. */
export const BUTTON_COLOR_STYLES: Record<ButtonColor, { bg: string; fg: string }> = {
  confy: { bg: '#2563EB', fg: '#ffffff' },
  telegram: { bg: '#229ED9', fg: '#ffffff' },
  youtube: { bg: '#D9251D', fg: '#ffffff' },
  viber: { bg: '#7360F2', fg: '#ffffff' },
  instagram: { bg: '#D62976', fg: '#ffffff' },
  green: { bg: '#16A34A', fg: '#ffffff' },
  dark: { bg: '#111827', fg: '#ffffff' },
  grey: { bg: '#E5E7EB', fg: '#111827' },
};

export interface MessageSpan {
  text: string;
  bold?: true;
  italic?: true;
  href?: string;
}

export interface MessageParagraph {
  type: 'paragraph';
  content: MessageSpan[];
}

export interface MessageButton {
  type: 'button';
  label: string;
  url: string;
  color: ButtonColor;
}

export type MessageBlock = MessageParagraph | MessageButton;

export interface RegistrationMessage {
  v: 1;
  blocks: MessageBlock[];
}

/** https only, no credentials, no whitespace; anything else is not a link. */
export function isSafeUrl(url: unknown): url is string {
  if (typeof url !== 'string' || url.length === 0 || url.length > URL_MAX || /\s/.test(url)) return false;
  if (!/^https:\/\//i.test(url)) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && !!parsed.hostname && !parsed.username && !parsed.password;
  } catch {
    return false;
  }
}

/** Accepts `t.me/x` or `www.site.ua` and adds https://; returns null when it still isn't a safe link. */
export function normalizeUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return isSafeUrl(withScheme) ? withScheme : null;
}

export function messageTextLength(message: RegistrationMessage): number {
  let length = 0;
  for (const block of message.blocks) {
    if (block.type === 'paragraph') for (const span of block.content) length += span.text.length;
  }
  return length;
}

export function messageButton(message: RegistrationMessage): MessageButton | null {
  return message.blocks.find((b): b is MessageButton => b.type === 'button') ?? null;
}

/** True when there is nothing to show: no text and no button. */
export function isMessageEmpty(message: RegistrationMessage): boolean {
  return message.blocks.every(b => b.type === 'paragraph' && b.content.every(s => s.text.trim() === ''));
}

export type MessageError = 'shape' | 'too_long' | 'bad_link' | 'bad_button' | 'empty';

/**
 * Parses a stored or submitted message. Same rules as the database CHECK:
 * known keys only, at most one button and only as the last block, text up to
 * MESSAGE_MAX_TEXT characters, https links, a known button color.
 */
export function parseMessage(value: unknown): { ok: true; message: RegistrationMessage } | { ok: false; error: MessageError } {
  if (!isPlainObject(value) || !onlyKeys(value, ['v', 'blocks']) || value.v !== 1 || !Array.isArray(value.blocks)) {
    return { ok: false, error: 'shape' };
  }
  const blocks: MessageBlock[] = [];
  let paragraphs = 0;
  for (let i = 0; i < value.blocks.length; i++) {
    const block: unknown = value.blocks[i];
    if (!isPlainObject(block)) return { ok: false, error: 'shape' };
    if (block.type === 'paragraph') {
      if (!onlyKeys(block, ['type', 'content']) || !Array.isArray(block.content)) return { ok: false, error: 'shape' };
      if (++paragraphs > MESSAGE_MAX_PARAGRAPHS) return { ok: false, error: 'too_long' };
      const content: MessageSpan[] = [];
      for (const span of block.content as unknown[]) {
        if (!isPlainObject(span) || !onlyKeys(span, ['text', 'bold', 'italic', 'href'])) return { ok: false, error: 'shape' };
        if (typeof span.text !== 'string' || span.text.length === 0) return { ok: false, error: 'shape' };
        if (span.bold !== undefined && span.bold !== true) return { ok: false, error: 'shape' };
        if (span.italic !== undefined && span.italic !== true) return { ok: false, error: 'shape' };
        if (span.href !== undefined && !isSafeUrl(span.href)) return { ok: false, error: 'bad_link' };
        content.push({
          text: span.text,
          ...(span.bold ? { bold: true as const } : {}),
          ...(span.italic ? { italic: true as const } : {}),
          ...(span.href !== undefined ? { href: span.href as string } : {}),
        });
      }
      blocks.push({ type: 'paragraph', content });
    } else if (block.type === 'button') {
      if (i !== value.blocks.length - 1 || !onlyKeys(block, ['type', 'label', 'url', 'color'])) return { ok: false, error: 'bad_button' };
      const label = typeof block.label === 'string' ? block.label.trim() : '';
      if (!label || label.length > BUTTON_MAX_LABEL || label !== block.label) return { ok: false, error: 'bad_button' };
      if (!isSafeUrl(block.url)) return { ok: false, error: 'bad_link' };
      if (!BUTTON_COLORS.includes(block.color as ButtonColor)) return { ok: false, error: 'bad_button' };
      blocks.push({ type: 'button', label, url: block.url as string, color: block.color as ButtonColor });
    } else {
      return { ok: false, error: 'shape' };
    }
  }
  const message: RegistrationMessage = { v: 1, blocks };
  if (messageTextLength(message) > MESSAGE_MAX_TEXT) return { ok: false, error: 'too_long' };
  if (isMessageEmpty(message)) return { ok: false, error: 'empty' };
  return { ok: true, message };
}

/** A stored value as a message, or null when it is missing or no longer valid. */
export function readMessage(value: unknown): RegistrationMessage | null {
  if (value === null || value === undefined) return null;
  const parsed = parseMessage(value);
  return parsed.ok ? parsed.message : null;
}

/** Color to preselect for a pasted button link; null leaves the organizer's choice alone. */
export function suggestButtonColor(url: string): ButtonColor | null {
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
  if (host === 't.me' || host === 'telegram.me' || host.endsWith('.t.me')) return 'telegram';
  if (host === 'youtube.com' || host.endsWith('.youtube.com') || host === 'youtu.be') return 'youtube';
  if (host === 'viber.com' || host.endsWith('.viber.com') || host === 'invite.viber.com') return 'viber';
  if (host === 'instagram.com' || host.endsWith('.instagram.com')) return 'instagram';
  return null;
}

/** The button's host as shown under it, so participants see where it leads. */
export function linkHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function onlyKeys(value: Record<string, unknown>, allowed: string[]): boolean {
  return Object.keys(value).every(k => allowed.includes(k));
}
